import clsx from 'clsx'
import React, {useEffect, useState} from 'react'
import * as Yup from 'yup'
import {Formik} from 'formik'
import useUtils from './useUtils'
import {useLocation, useNavigate} from 'react-router-dom'
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider'
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment'
import {DateTimePicker} from '@mui/x-date-pickers/DateTimePicker'
import TextField from '@mui/material/TextField'
import moment from 'moment'
import {setTitle} from '../../redux/reducers/mainSlice'
import {useDispatch} from 'react-redux'
import copyIcon from '../../../_metronic/assets/custom-icons/copyIcon.png'
import AssignConfirmation from '../../modules/alert/AssignConfirmation'
import CustomFilterDropdown from '../../modules/common/CustomFilterDropdown'

const AddSchedule = () => {
  const dispatch = useDispatch()
  dispatch(setTitle('Schedule'))
  const location = useLocation()
  const locationState = location?.state?.result || null
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)

  // Dropdown options
  const [cohortOptions, setCohortOptions] = useState([])
  
  // Token-based pagination states
  const [cohortNextPageToken, setCohortNextPageToken] = useState(null)
  
  // Loading states
  const [isLoadingCohorts, setIsLoadingCohorts] = useState(false)

  const {fetchCohort, submitSchedule, modifySchedule} = useUtils()

  const validationSchema = Yup.object().shape({
    topic: Yup.string().required('Please enter topic'),
    cohortId: Yup.string().required('Please select cohort'),
    zoomMeetingId: Yup.string()
      .required('Please enter Zoom Meeting ID')
      .matches(/^\d+$/, 'Zoom Meeting ID must be a numeric value'),
    zoomStartLink: Yup.string()
      .required('Please enter Zoom Start Link')
      .url('Zoom start link must be a valid URL'),
    zoomJoinLink: Yup.string()
      .required('Please enter Zoom Join Link')
      .url('Zoom join link must be a valid URL'),
    endTime: Yup.mixed().test(
      'is-end-time-valid',
      'End time must be later than start time',
      function (value) {
        const {startTime} = this.parent
        return !startTime || !value || moment(value).isAfter(moment(startTime))
      }
    ),
  })

  const initialValues = locationState
    ? {
        id: locationState?.id,
        isEdit: true,
        topic: locationState?.topic,
        zoomMeetingId: locationState?.zoomMeetingId,
        zoomStartLink: locationState?.zoomStartLink,
        zoomJoinLink: locationState?.zoomJoinLink,
        cohortId: locationState?.cohort?.id,
        cohortaName: locationState?.cohort?.title,
        startTime: moment(Number(locationState?.startTime)),
        endTime: moment(Number(locationState?.endTime)),
        isForBlackforceMember: locationState?.isForBlackforceMember,
      }
    : {
        topic: '',
        zoomMeetingId: '',
        zoomStartLink: '',
        zoomJoinLink: '',
        cohortId: cohortOptions[0]?.id,
        cohortaName: cohortOptions[0]?.title ?? '',
        startTime: moment().add(10, 'minutes'),
        endTime: moment().add(50, 'minutes'),
        isForBlackforceMember: false,
      }

  // Load more function for token-based pagination
  const loadMoreCohorts = async () => {
    if (isLoadingCohorts || !cohortNextPageToken) return

    setIsLoadingCohorts(true)
    try {
      const response = await fetchCohort(cohortNextPageToken)
      if (response) {
        setCohortOptions((prev) => [...prev, ...response.records])
        setCohortNextPageToken(response.nextPageToken)
      }
    } catch (error) {
      console.error('Error loading more cohorts:', error)
    } finally {
      setIsLoadingCohorts(false)
    }
  }

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const cohortResponse = await fetchCohort()
        if (cohortResponse) {
          setCohortOptions(cohortResponse.records)
          setCohortNextPageToken(cohortResponse.nextPageToken)
        }
      } catch (error) {
        console.error('Error fetching data:', error)
      }
    }
    fetchInitialData()
  }, [])

  const handleSubmit = (values) => {
    const data = {
      topic: values.topic,
      zoomLink: values.zoomLink,
      zoomMeetingId: values.zoomMeetingId,
      zoomStartLink: values.zoomStartLink,
      zoomJoinLink: values.zoomJoinLink,
      cohortId: values.cohortId,
      startTime: values.startTime.valueOf(),
      endTime: values.endTime.valueOf(),
      isForBlackforceMember: values.isForBlackforceMember,
    }

    if (values.isEdit) {
      modifySchedule(values.id, data)
    } else {
      submitSchedule(data)
    }
  }

  return (
    <>
      <div className='card card-custom'>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({values, touched, handleChange, errors, handleBlur, handleSubmit, setFieldValue}) => (
            <form className='form' onSubmit={handleSubmit}>
              <div className='card-body'>
                <div className='tab-content pt-3'>
                  <div className='row align-items-start flex-wrap'>
                    <div className='col'>
                      <label className='col-form-label required fw-bold fs-6'>Topic</label>
                      <input
                        type='text'
                        className='form-control form-control-lg form-control-solid mb-3 mb-lg-0'
                        placeholder='Topic'
                        name='topic'
                        value={values.topic}
                        onChange={handleChange}
                      />
                      {touched.topic && errors.topic && (
                        <div className='fv-plugins-message-container'>
                          <span role='alert' className='text-danger'>
                            {errors.topic}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className='col'>
                      <label className='col-form-label required fw-bold fs-6'>Cohort</label>
                      <CustomFilterDropdown
                        options={cohortOptions}
                        selectedValue={values.cohortId}
                        onChange={(id) => setFieldValue('cohortId', id)}
                        onScrollEnd={loadMoreCohorts}
                        isLoading={isLoadingCohorts}
                        placeholder='Select Cohort'
                        displayField='title'
                        showSearch={true}
                      />
                      {touched.cohortId && errors.cohortId && (
                        <div className='fv-plugins-message-container'>
                          <span role='alert' className='text-danger'>
                            {errors.cohortId}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    <div className='col'>
                      <label className='col-form-label fw-bold fs-6 d-block'>
                        For BlackForce Member
                      </label>
                      <label className='form-check d-inline-block form-check-custom form-check-solid form-switch mt-2'>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          checked={values?.isForBlackforceMember}
                          onClick={() =>
                            setFieldValue('isForBlackforceMember', !values?.isForBlackforceMember)
                          }
                        />
                      </label>
                    </div>
                  </div>

                  <div className='row align-items-center flex-wrap mt-7'>
                    <div className='col'>
                      <label className='col-form-label required fw-bold fs-6'>
                        Zoom Meeting ID
                      </label>
                      <input
                        type='text'
                        className='form-control form-control-lg form-control-solid mb-3 mb-lg-0'
                        placeholder='Zoom Meeting ID'
                        name='zoomMeetingId'
                        value={values.zoomMeetingId}
                        onChange={(e) => {
                          const onlyNums = e.target.value.replace(/[^0-9]/g, '')
                          setFieldValue('zoomMeetingId', onlyNums)
                        }}
                      />
                      {touched.zoomMeetingId && errors.zoomMeetingId && (
                        <div className='fv-plugins-message-container'>
                          <span role='alert' className='text-danger'>
                            {errors.zoomMeetingId}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className='col'>
                      <label className='col-form-label required fw-bold fs-6'>
                        Zoom Start Link
                      </label>
                      <input
                        type='text'
                        className='form-control form-control-lg form-control-solid mb-3 mb-lg-0'
                        placeholder='Zoom Start Link'
                        name='zoomStartLink'
                        value={values.zoomStartLink}
                        onChange={handleChange}
                      />
                      {touched.zoomStartLink && errors.zoomStartLink && (
                        <div className='fv-plugins-message-container'>
                          <span role='alert' className='text-danger'>
                            {errors.zoomStartLink}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className='col'>
                      <label className='col-form-label required fw-bold fs-6'>Zoom Join Link</label>
                      <input
                        type='text'
                        className='form-control form-control-lg form-control-solid mb-3 mb-lg-0'
                        placeholder='Zoom Join Link'
                        name='zoomJoinLink'
                        value={values.zoomJoinLink}
                        onChange={handleChange}
                      />
                      {touched.zoomJoinLink && errors.zoomJoinLink && (
                        <div className='fv-plugins-message-container'>
                          <span role='alert' className='text-danger'>
                            {errors.zoomJoinLink}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className='row align-items-center mt-7'>
                    <div className='col-4'>
                      <label className='col-form-label required fw-bold fs-6 d-block'>
                        Start Time
                      </label>
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DateTimePicker
                          value={values.startTime}
                          onChange={(date) => {
                            setFieldValue('startTime', date)
                          }}
                          renderInput={(params) => <TextField {...params} fullWidth />}
                        />
                      </LocalizationProvider>
                    </div>

                    <div className='col-4'>
                      <label className='col-form-label required fw-bold fs-6 d-block'>
                        End Time
                      </label>
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DateTimePicker
                          value={values.endTime}
                          onChange={(date) => {
                            setFieldValue('endTime', date)
                          }}
                          renderInput={(params) => <TextField {...params} fullWidth />}
                        />
                      </LocalizationProvider>
                    </div>
                    {touched.endTime && errors.endTime && (
                      <div className='fv-plugins-message-container'>
                        <span role='alert' className='text-danger'>
                          {errors.endTime}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className='d-flex align-items-center justify-content-end my-5'>
                <button
                  type='button'
                  className='btn btn-danger ms-2 mt-2 me-2'
                  onClick={() => navigate('/schedulelist')}
                >
                  Cancel
                </button>
                <button
                  type='button'
                  onClick={() => {
                    if (values?.isEdit) {
                      setIsOpen(true) // Open the confirmation modal
                    } else {
                      document.querySelector('form')?.requestSubmit() // Directly submit in create mode
                    }
                  }}
                  className='btn btn-primary ms-2 mt-2 me-2'
                >
                  {!values.isEdit ? 'Submit' : 'Update'}
                </button>
              </div>
            </form>
          )}
        </Formik>
      </div>
      <AssignConfirmation
        isVisible={isOpen}
        message='Are you sure you want to update this Schedule?'
        onConfirm={() => {
          setIsOpen(false)
          setTimeout(() => {
            document.querySelector('form')?.requestSubmit() // Triggers form submission
          }, 0)
        }}
        onCancel={() => setIsOpen(false)}
      />
    </>
  )
}

export default AddSchedule